/* sp-cart.js (F20) - Sucker Punch floating cart drawer + cart page + shipping-protection toggle.
   One shared engine for the drawer (snippets/sp-cart.liquid) and the cart page (sections/sp-cart-page.liquid).
   Protection rules:
   - cart has other items, protection missing, shopper has not turned it off (localStorage spProtOff) -> add it
   - any line with a selling plan -> Member variant WITH the membership plan; otherwise One-time variant, no plan
   - composition changes -> swap variants; protection is the only line -> remove it
   - toggle off -> remove + set spProtOff; toggle on -> add + clear spProtOff
   - the protection line is never rendered; totals (cart.total_price) include it.
   2026-10-09 (optimizer F1): the line's photo and name are plain (not links to the product page). Five recorded visitors
   tapped the name right after adding a bag and were carried out of the cart; none came back to check out.
   2026-10-09 (optimizer F8): a cart of one-time items shows its shipping charge up front, read from Shopify (see shipQuote).
   2026-10-09 (optimizer F9): the drawer fits a phone. Only shipping protection, the totals and Checkout stay pinned; the lines and
   the membership offer scroll together above them (.spc-main). The offer is one compact block, the one-time protection text opens
   on a tap of its label. No wording, price, default or behavior changed. Styles: the F9 block at the end of sp-cart.css.
   2026-10-10 (Davision p.2): a membership cart lists the two member gifts as free lines under the box (giftRows). They are
   display only: the Flow "SP member gifts" adds them to the order after purchase, so the cart, its total and checkout are
   unchanged. The two gift pills under "Member perks" became those lines. */
(function () {
  'use strict';
  if (window.SPCart) return;

  var root = document.getElementById('sp-cart-config');
  var CFG = root ? JSON.parse(root.textContent) : {};
  var PROT_PID = Number(CFG.protProductId || 15405324927078);
  var V_ONCE = Number(CFG.protOnceVariant || 67618476097638);
  var V_MEMBER = Number(CFG.protMemberVariant || 67618476130406);
  var MEMBER_PLAN = Number(CFG.memberPlan || 5837193318);
  var MEMBER_PRICE = Number(CFG.memberPriceCents || 0); /* 2026-10-09 (optimizer F5): real plan price from Liquid; 0 = offer hidden */
  var OFF_KEY = 'spProtOff';
  var CART_URL = (window.routes && window.routes.cart_url) || '/cart';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var origFetch = window.fetch.bind(window);
  var state = { cart: null, busy: false, pending: false, lastSync: 0 };
  var protInfo = false; /* 2026-10-09 (optimizer F9): is the one-time protection description open in the drawer */
  var listeners = [];

  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { if (v === null) window.localStorage.removeItem(k); else window.localStorage.setItem(k, v); } catch (e) {} }
  function protOff() { return lsGet(OFF_KEY) === '1'; }

  function api(path, body) {
    var opts = { credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-SP-Cart': '1' } };
    if (body) { opts.method = 'POST'; opts.body = JSON.stringify(body); }
    return origFetch(ROOT + path.replace(/^\//, ''), opts).then(function (r) {
      return r.json().then(function (j) { if (!r.ok) { var e = new Error(j.description || j.message || r.status); e.data = j; throw e; } return j; });
    });
  }
  function getCart() { return api('cart.js?_=' + Date.now()); }

  function isProt(item) { return Number(item.product_id) === PROT_PID; }
  function others(cart) { return cart.items.filter(function (i) { return !isProt(i); }); }
  function protLines(cart) { return cart.items.filter(isProt); }
  function itemCount(cart) { return others(cart).reduce(function (n, i) { return n + i.quantity; }, 0); }

  /* a cart holds membership lines OR one-time lines, never both: a one-time line makes Shopify charge shipping on renewals */
  function planLines(cart) { return others(cart).filter(function (i) { return !!i.selling_plan_allocation; }); }
  function onceLines(cart) { return others(cart).filter(function (i) { return !i.selling_plan_allocation; }); }
  function isMixed(cart) { return !!cart && planLines(cart).length > 0 && onceLines(cart).length > 0; }
  function resolveMix(keep) {
    if (!state.cart) return Promise.resolve();
    var drop = keep === 'member' ? onceLines(state.cart) : planLines(state.cart);
    var updates = {};
    drop.forEach(function (l) { updates[l.key] = 0; });
    state.busy = true; emit();
    return api('cart/update.js', { updates: updates }).catch(function (e) { console.warn('[sp-cart] resolve failed', e); })
      .then(function () { state.busy = false; return sync(); });
  }

  /* what protection line (if any) the cart should hold */
  function wanted(cart) {
    var o = others(cart);
    if (!o.length || protOff()) return null;
    var member = o.some(function (i) { return !!i.selling_plan_allocation; });
    return member ? { id: V_MEMBER, plan: MEMBER_PLAN, member: true } : { id: V_ONCE, plan: null, member: false };
  }
  function lineMatches(line, want) {
    if (!line || !want) return false;
    var plan = line.selling_plan_allocation ? Number(line.selling_plan_allocation.selling_plan.id) : null;
    return Number(line.variant_id) === want.id && line.quantity === 1 && plan === want.plan;
  }

  /* bring protection in line with the cart; returns the final cart */
  function reconcile(cart) {
    var want = wanted(cart);
    var lines = protLines(cart);
    if (!want && !lines.length) return Promise.resolve(cart);
    if (want && lines.length === 1 && lineMatches(lines[0], want)) return Promise.resolve(cart);
    var updates = {};
    lines.forEach(function (l) { updates[l.key] = 0; });
    var p = lines.length ? api('cart/update.js', { updates: updates }) : Promise.resolve(null);
    return p.then(function () {
      if (!want) return null;
      var item = { id: want.id, quantity: 1 };
      if (want.plan) item.selling_plan = want.plan;
      return api('cart/add.js', { items: [item] });
    }).then(getCart);
  }

  /* serialized sync: one at a time, re-run once if something changed meanwhile */
  var syncPromise = null;
  function sync() {
    if (state.busy) { state.pending = true; return syncPromise; }
    state.busy = true;
    emit();
    syncPromise = getCart().then(reconcile).then(function (cart) {
      state.cart = cart;
    }).catch(function (e) {
      console.warn('[sp-cart] sync failed', e);
      return getCart().then(function (c) { state.cart = c; }).catch(function () {});
    }).then(function () {
      state.busy = false;
      state.lastSync = Date.now();
      if (state.pending) { state.pending = false; return sync(); }
      emit();
    });
    return syncPromise;
  }

  var syncTimer = null;
  function scheduleSync(delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, delay == null ? 120 : delay);
  }

  function setOff(off) {
    lsSet(OFF_KEY, off ? '1' : null);
    return sync();
  }
  /* 2026-10-09 (optimizer F5): a cart that holds ONLY one-time 30-Packs can become a membership cart in one tap.
     4 of 5 recorded visitors who reached checkout and did not pay had a one-time cart (shipping is charged under $50). */
  function canJoin(cart) {
    var o = cart ? others(cart) : [];
    return MEMBER_PRICE > 0 && o.length > 0 && o.every(function (i) { return !i.selling_plan_allocation && /^30-pack-/.test(i.handle || ''); });
  }
  function joinAll() {
    if (!state.cart || !canJoin(state.cart)) return Promise.resolve();
    var count = others(state.cart).length;
    state.busy = true; emit();
    var p = Promise.resolve();
    for (var k = 0; k < count; k++) {
      p = p.then(getCart).then(function (c) {
        /* line keys change when a plan is set, so find the next one-time 30-Pack line fresh each time */
        var it = others(c).filter(function (i) { return !i.selling_plan_allocation && /^30-pack-/.test(i.handle || ''); })[0];
        if (!it) return null;
        return api('cart/change.js', { id: it.key, quantity: it.quantity, selling_plan: MEMBER_PLAN });
      });
    }
    return p.catch(function (e) { console.warn('[sp-cart] join failed', e); }).then(function () { state.busy = false; return sync(); });
  }
  /* 2026-10-09 (optimizer F8): the shipping charge of a ONE-TIME cart, shown in the cart instead of first appearing at checkout.
     2026-10-19: no longer read from cart/shipping_rates.json. That call saved its reference address (Honolulu 96813 / Utah 84604)
     onto the shopper's cart, so checkout opened with a ZIP and state that were not theirs. The rate now mirrors Shopify
     Settings > Shipping (General profile, checked 10/19): US Economy $10.50, free on orders of $50.00 and up.
     If those rates change in admin, change these two numbers. Membership carts never show it: members ship free. */
  var SHIP_FLAT = 1050, SHIP_FREE_AT = 5000;
  function shipWanted(cart) { return !!cart && onceLines(cart).length > 0 && planLines(cart).length === 0; }
  /* returns {lo: cents, flat: true} for a one-time cart, else null */
  function shipQuote(st) {
    var cart = st.cart;
    if (!shipWanted(cart)) return null;
    return { lo: cart.total_price >= SHIP_FREE_AT ? 0 : SHIP_FLAT, flat: true };
  }

  function changeQty(key, qty) {
    state.busy = true; emit();
    return api('cart/change.js', { id: key, quantity: qty }).catch(function (e) { console.warn('[sp-cart] change failed', e); })
      .then(function () { state.busy = false; return sync(); });
  }

  function emit() { listeners.forEach(function (fn) { try { fn(state); } catch (e) { console.error(e); } }); }
  function onChange(fn) { listeners.push(fn); if (state.cart) fn(state); }

  /* ---------- formatting ---------- */
  function money(cents, cur) {
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD' }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function img(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function names(item) {
    var t = item.product_title || item.title || '';
    var m = t.match(/^(.*?)\s+-\s+(\d+\s*mg)\s+(.+)$/i);
    var name = t, flavor = '';
    if (m) { name = m[1].replace(/(\d+)\s+Pack/i, '$1-Pack'); flavor = m[3]; }
    if (item.variant_title && !/default title/i.test(item.variant_title)) flavor = flavor ? flavor + ' · ' + item.variant_title : item.variant_title;
    return { name: name, flavor: flavor };
  }
  function planLabel(item) {
    var a = item.selling_plan_allocation;
    if (!a) return '';
    var n = (a.selling_plan && a.selling_plan.name) || '';
    var every = n.match(/every\s+(\d+\s+\w+)/i);
    var head = /membership/i.test(n) ? 'Membership' : n.split(/\s+-\s+/)[0];
    return head + (every ? ' · every ' + every[1] : '');
  }
  function isMemberLine(item) {
    var a = item.selling_plan_allocation;
    return !!a && Number(a.selling_plan.id) === MEMBER_PLAN;
  }

  /* 2026-10-10: the member gifts as free cart lines. Mints tin = box 1, extra 10-Pack = box 2 (the gift Flow's order).
     CFG.giftMints / CFG.giftPack are null when the signed-in customer already got that gift; giftPack.now = the mints
     were already given, so the 10-Pack is the gift on this order. */
  function giftRow(g, name, sub, when, cur) {
    return '<li class="spc-line spc-line--gift">' +
      '<span class="spc-thumb">' + (g.img ? '<img src="' + esc(g.img) + '" alt="' + esc(name) + '" width="90" height="90" loading="lazy">' : '') + '</span>' +
      '<div class="spc-info">' +
        '<span class="spc-name">' + esc(name) + '</span>' +
        '<p class="spc-flavor">' + esc(sub) + '</p>' +
        '<p class="spc-gift-when"><span class="spc-tag spc-tag--gift">Free gift</span> ' + esc(when) + '</p>' +
      '</div>' +
      '<div class="spc-price">' + (g.priceCents > 0 ? '<s class="spc-was">' + money(g.priceCents, cur) + '</s>' : '') + '<span>Free</span></div>' +
    '</li>';
  }
  function giftRows(cart) {
    if (!others(cart).some(isMemberLine)) return '';
    var h = '', cur = cart.currency;
    if (CFG.giftMints) h += giftRow(CFG.giftMints, 'Caffeinated Mints', '1 tin', 'Ships with this box.', cur);
    if (CFG.giftPack) h += giftRow(CFG.giftPack, '10-Pack', 'Strawberry', CFG.giftPack.now ? 'Ships with this box.' : 'Ships with your next box.', cur);
    return h;
  }

  /* ---------- rendering (shared by drawer + page) ---------- */
  function renderInto(el, st, opts) {
    var cart = st.cart;
    if (!cart) { el.innerHTML = st.adding ? '<p class="spc-loading spc-adding" role="status">Adding it to your cart…</p>' : '<p class="spc-loading">Loading your cart…</p>'; return; }
    var cur = cart.currency;
    var lines = others(cart);
    var prot = protLines(cart)[0];
    var want = wanted(cart);
    var member = lines.some(function (i) { return !!i.selling_plan_allocation; });
    el.classList.toggle('is-busy', !!st.busy);

    if (!lines.length) {
      /* F2 2026-10-12: while an add is on its way, say so instead of "Your cart is empty." */
      if (st.adding) { el.innerHTML = '<p class="spc-loading spc-adding" role="status">Adding it to your cart…</p>'; return; }
      el.innerHTML = '<div class="spc-empty"><p class="spc-empty-h">Your cart is empty.</p>' +
        '<a class="spc-btn" href="' + esc(CFG.shopUrl || '/collections/all') + '">Shop now</a></div>';
      return;
    }

    var h = '<ul class="spc-lines" role="list">';
    lines.forEach(function (it) {
      var n = names(it);
      var pl = planLabel(it);
      var strike = it.original_line_price > it.final_line_price ? '<s class="spc-was">' + money(it.original_line_price, cur) + '</s>' : '';
      h += '<li class="spc-line" data-key="' + esc(it.key) + '">' +
        '<span class="spc-thumb">' + (it.image ? '<img src="' + esc(img(it.image, 180)) + '" alt="' + esc(it.product_title) + '" width="90" height="90" loading="lazy">' : '') + '</span>' +
        '<div class="spc-info">' +
          '<span class="spc-name">' + esc(n.name) + '</span>' +
          (n.flavor ? '<p class="spc-flavor">' + esc(n.flavor) + '</p>' : '') +
          (pl ? '<p class="spc-plan"><span class="spc-tag">' + esc(pl) + '</span></p>' : '') +
          (isMemberLine(it) ? '<div class="spc-perks"><p class="spc-perks-h">Member perks</p><ul role="list"><li>Free shipping every box</li></ul></div>' : '') +
          '<div class="spc-row">' +
            '<div class="spc-qty" role="group" aria-label="Quantity for ' + esc(n.name) + '">' +
              '<button type="button" class="spc-q" data-act="dec" aria-label="Decrease quantity">−</button>' +
              '<span class="spc-qn" aria-live="polite">' + it.quantity + '</span>' +
              '<button type="button" class="spc-q" data-act="inc" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="spc-rm" data-act="rm">Remove</button>' +
          '</div>' +
        '</div>' +
        '<div class="spc-price">' + strike + '<span>' + money(it.final_line_price, cur) + '</span></div>' +
      '</li>';
    });
    h += giftRows(cart);
    h += '</ul>';

    var on2 = !protOff();
    var mixed = isMixed(cart);
    var sq = mixed ? null : shipQuote(st);
    var paid = !!sq && sq.lo > 0;
    /* 2026-10-09 (optimizer F9): in the drawer the offer (and the mixed-cart choice) scroll with the lines; on the cart page they stay in the summary */
    var dr = !!(opts && opts.drawer);
    var joinHtml = canJoin(cart) ? '<div class="spc-join">' +
          '<p class="spc-join-h">Members pay ' + money(MEMBER_PRICE, cur) + ' a bag</p>' +
          '<p class="spc-join-t">' + (paid && sq.flat ? 'Members ship free: no ' + money(sq.lo, cur) + '. ' : 'Members ship free on every box. ') + money(MEMBER_PRICE, cur) + ' every 4 weeks until you cancel from your account page.</p>' +
          '<button type="button" class="spc-btn spc-btn--sm" data-act="join" aria-label="Switch to the membership">Switch</button>' +
        '</div>' : '';
    var mixHtml = mixed ? '<div class="spc-mix" role="alert" tabindex="-1">' +
          '<p class="spc-mix-t">Member boxes check out on their own so shipping stays free.</p>' +
          '<div class="spc-mix-b"><button type="button" class="spc-btn spc-btn--sm" data-act="mix-member">Keep my membership</button>' +
          '<button type="button" class="spc-btn spc-btn--sm spc-btn--plain" data-act="mix-once">Switch to one-time items</button></div>' +
        '</div>' : '';
    var tight = dr && !member; /* one-time cart in the drawer: the protection description sits behind a tap on its label */
    if (dr) h = '<div class="spc-main">' + mixHtml + h + joinHtml + '</div>';
    h += '<div class="spc-foot">' +
      (dr ? '' : joinHtml) +
      '<div class="spc-prot' + (on2 ? ' is-on' : '') + (tight ? ' spc-prot--c' : '') + '">' +
        '<button type="button" class="spc-switch" role="switch" aria-checked="' + on2 + '" data-act="prot" aria-label="Shipping protection">' +
          '<span class="spc-knob"></span></button>' +
        (tight
          ? '<button type="button" class="spc-prot-b" data-act="prot-info" aria-expanded="' + protInfo + '" aria-controls="spc-prot-info">' +
              '<span class="spc-prot-h">Shipping protection <b>' + money(500, cur) + '</b></span><span class="spc-prot-q" aria-hidden="true">i</span></button>' +
            '<p class="spc-prot-i" id="spc-prot-info"' + (protInfo ? '' : ' hidden') + '>Covers your order if it’s lost, stolen, or damaged in shipping.</p>'
          : '<div class="spc-prot-txt"><p class="spc-prot-h">Shipping protection <b>' + money(500, cur) + (member ? ' per box' : '') + '</b></p>' +
            '<p class="spc-prot-i">' + (member
              ? 'Covers every box if it’s lost, stolen, or damaged in shipping. Off = no charge now or later.'
              : 'Covers your order if it’s lost, stolen, or damaged in shipping.') + '</p></div>') +
      '</div>' +
      '<div class="spc-total"><span>Subtotal</span><b>' + money(cart.total_price, cur) + '</b></div>' +
      (sq
        ? '<div class="spc-ship"><span>Shipping</span><b>' + (paid ? (sq.flat ? '' : 'from ') + money(sq.lo, cur) : 'Free') + '</b></div>' +
          '<p class="spc-note">' + (paid ? (sq.flat ? 'Ships free over $50. Taxes calculated at checkout.' : 'Exact amount and taxes calculated at checkout. Ships free over $50.') : 'Taxes calculated at checkout.') + '</p>'
        : '<p class="spc-note">Shipping &amp; taxes calculated at checkout.</p>') +
      (dr ? '' : mixHtml) +
      '<form action="' + esc(CART_URL) + '" method="post" class="spc-co">' +
        '<button type="submit" name="checkout" class="spc-btn spc-btn--full"' + (mixed ? ' disabled aria-disabled="true"' : (st.busy ? ' aria-disabled="true"' : '')) + '>Checkout</button>' +
      '</form>' +
      (opts && opts.drawer ? '<a class="spc-viewcart" href="' + esc(CART_URL) + '">View cart</a>' : '') +
    '</div>';
    /* F9: a re-render (quantity change, shipping read) must not throw the scrolled list back to the top */
    var sc = dr ? el.querySelector('.spc-main') : null, keep = sc ? sc.scrollTop : 0;
    el.innerHTML = h;
    if (keep) { sc = el.querySelector('.spc-main'); if (sc) sc.scrollTop = keep; }
  }

  function bindActions(el) {
    el.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act]');
      if (!b || !el.contains(b)) return;
      var act = b.getAttribute('data-act');
      if (act === 'prot') { e.preventDefault(); setOff(b.getAttribute('aria-checked') === 'true'); return; }
      if (act === 'prot-info') { /* F9: show / hide the protection description, no re-render */
        e.preventDefault(); protInfo = !protInfo;
        b.setAttribute('aria-expanded', String(protInfo));
        var inf = el.querySelector('#spc-prot-info'); if (inf) inf.hidden = !protInfo;
        return;
      }
      if (act === 'join') { e.preventDefault(); joinAll(); return; }
      if (act === 'mix-member' || act === 'mix-once') { e.preventDefault(); resolveMix(act === 'mix-member' ? 'member' : 'once'); return; }
      var li = b.closest('.spc-line'); if (!li) return;
      var key = li.getAttribute('data-key');
      var it = state.cart && state.cart.items.filter(function (i) { return i.key === key; })[0];
      if (!it) return;
      e.preventDefault();
      if (act === 'inc') changeQty(key, it.quantity + 1);
      else if (act === 'dec') changeQty(key, it.quantity - 1);
      else if (act === 'rm') changeQty(key, 0);
    });
    el.addEventListener('submit', function (e) {
      var f = e.target.closest('.spc-co'); if (!f) return;
      if (isMixed(state.cart)) { e.preventDefault(); var mx = el.querySelector('.spc-mix'); if (mx) { mx.scrollIntoView({ block: 'nearest' }); mx.focus(); } return; }
      if (state.busy || state.pending) { e.preventDefault(); sync().then(function () { if (!isMixed(state.cart)) f.submit(); }); }
    });
  }

  /* ---------- header bubble + floating button counts (protection excluded) ---------- */
  function paintCounts(st) {
    if (!st.cart) return;
    var n = itemCount(st.cart);
    document.querySelectorAll('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]').forEach(function (s) { s.textContent = n; });
    document.querySelectorAll('#cart-icon-bubble .cart-count-bubble').forEach(function (b) { b.style.display = n ? '' : 'none'; });
    document.querySelectorAll('[data-spc-count]').forEach(function (b) { b.textContent = n; b.hidden = !n; });
    /* 2026-10-09 (optimizer F10): the floating cart button exists only while the cart holds something */
    var fab = document.querySelector('.spc-fab');
    if (fab && fab.hidden !== !n) { fab.hidden = !n; if (n) queueFab(); }
  }

  /* ---------- drawer ---------- */
  var drawer, lastFocus;
  function openDrawer() {
    if (!drawer) return false;
    if (document.body.classList.contains('spc-on-cart-page')) return false;
    var wasOpen = !drawer.hidden && drawer.classList.contains('is-open');
    var ae = document.activeElement;
    if (!wasOpen && ae && !drawer.contains(ae)) lastFocus = ae;
    drawer.hidden = false;
    requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    document.documentElement.classList.add('spc-lock');
    var c = drawer.querySelector('.spc-close'); if (c && !wasOpen) c.focus();
    scheduleSync(0);
    return true;
  }
  function closeDrawer() {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('spc-lock');
    setTimeout(function () { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, 260);
    var back = lastFocus && document.contains(lastFocus) ? lastFocus : document.querySelector('.spc-fab, #sps-hdr .hdr-cart, #cart-icon-bubble');
    if (back && back.focus) back.focus();
    lastFocus = null;
  }
  function drawerOpen() { return !!drawer && !drawer.hidden && drawer.classList.contains('is-open'); }
  function focusables() {
    return Array.prototype.filter.call(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null && n.tabIndex !== -1; });
  }
  /* keep keyboard focus inside the open drawer: Tab / Shift+Tab cycle, Escape closes */
  function onDrawerKey(e) {
    if (!drawerOpen()) return;
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables(); if (!f.length) { e.preventDefault(); return; }
    var first = f[0], last = f[f.length - 1], a = document.activeElement;
    if (!drawer.contains(a)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && a === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
  }
  /* a re-render replaces the buttons; if that dropped focus out of the drawer, put it back */
  function keepFocusIn() {
    if (!drawerOpen() || drawer.contains(document.activeElement)) return;
    var mx = drawer.querySelector('.spc-mix'), c = drawer.querySelector('.spc-close');
    if (mx) mx.focus(); else if (c) c.focus();
  }

  /* ---------- F2 2026-10-12 (optimizer, key cart-add-shows-adding-not-empty) ----------
     The drawer used to open only when the add came back, and then read "Your cart is empty. Shop now" until the cart was
     re-read (0.8 s on a fast line, 1 to 4 s on a slow one). Now it opens the moment the add is sent and reads
     "Adding it to your cart..." until the cart holding the new item has been read. A failed add closes it again (if this
     add opened it), so the empty message is never left up under a buy tap. Nothing here runs on the /cart page. */
  var addingTimer = null, openedForAdd = false;
  function addingEnd(ok) {
    if (!state.adding) return;
    clearTimeout(addingTimer);
    function clear() {
      state.adding = false;
      if (!ok && openedForAdd && state.cart && !others(state.cart).length) closeDrawer();
      openedForAdd = false;
      emit();
    }
    if (ok) { var s = sync(); if (s && s.then) s.then(clear, clear); else clear(); }
    else clear();
  }
  function addingStart() {
    if (!drawer || document.body.classList.contains('spc-on-cart-page')) return;
    state.adding = true;
    clearTimeout(addingTimer);
    addingTimer = setTimeout(function () { addingEnd(false); }, 15000);
    if (!drawerOpen()) openedForAdd = openDrawer() !== false;
    emit();
  }

  /* ---------- watch every other cart write on the page (theme forms, apps) ---------- */
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    var mine = init && init.headers && (init.headers['X-SP-Cart'] || (init.headers.get && init.headers.get('X-SP-Cart')));
    var p = origFetch(input, init);
    if (!mine && method === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
      var isAdd = /\/cart\/add/.test(url);
      if (isAdd) addingStart();
      p.then(function (r) { if (r.ok) { scheduleSync(150); if (isAdd) openDrawer(); } if (isAdd) addingEnd(r.ok); }).catch(function () { if (isAdd) addingEnd(false); });
    }
    return p;
  };

  /* site header (sp-site-header) ships an empty <cart-notification>; when the old theme header's script is not on the
     page, define it here so the theme's product-form.js keeps working (add -> our drawer, no redirect to /cart) */
  function defineStandIn() {
    if (!window.customElements || customElements.get('cart-notification')) return;
    customElements.define('cart-notification', class extends HTMLElement {
      getSectionsToRender() { return []; }
      setActiveElement(el) { this.activeElement = el; }
      renderContents() { if (!openDrawer()) scheduleSync(0); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', defineStandIn); else defineStandIn();

  /* Dawn cart-notification: keep the bubble update, show our drawer instead of the popup */
  if (window.customElements) {
    customElements.whenDefined('cart-notification').then(function () {
      var C = customElements.get('cart-notification');
      if (!C || C.prototype.__spPatched) return;
      C.prototype.__spPatched = true;
      C.prototype.renderContents = function (parsed) {
        try {
          var b = document.getElementById('cart-icon-bubble');
          if (b && parsed.sections && parsed.sections['cart-icon-bubble']) {
            b.innerHTML = this.getSectionInnerHTML(parsed.sections['cart-icon-bubble'], '.shopify-section');
          }
        } catch (e) {}
        if (!openDrawer()) scheduleSync(0);
      };
    });
  }

  /* keep the floating button clear of any fixed bottom bar (e.g. the homepage sticky bar) */
  var fabCtas = null;
  function placeFab() {
    var fab = document.querySelector('.spc-fab');
    if (!fab || fab.hidden || !document.elementsFromPoint) return; /* F10: nothing to place while the cart is empty */
    var vh = window.innerHeight, vw = window.innerWidth, top = vh, seen = [];
    [vw - 46, vw - 120, 40].forEach(function (x) {
      document.elementsFromPoint(x, vh - 8).forEach(function (el) {
        if (fab.contains(el) || el.closest('#sp-cart-drawer')) return;
        for (var n = el; n && n !== document.body; n = n.parentElement) {
          if (seen.indexOf(n) > -1) break;
          seen.push(n);
          var cs = getComputedStyle(n);
          if (cs.position === 'fixed' || cs.position === 'sticky') {
            var r = n.getBoundingClientRect();
            if (r.height > 0 && r.height < vh * 0.4 && r.bottom >= vh - 4 && r.top < top) top = r.top;
            break;
          }
        }
      });
    });
    var lift = top < vh ? Math.round(vh - top + 12) : 16;
    /* F10: resting at the bottom edge the stylesheet adds the phone's safe area; above a bar the measured gap already holds it */
    fab.style.bottom = top < vh ? lift + 'px' : '';
    /* F10: never sit on a buy button or a main button. While one is under the button's spot (and not inside a bar, the menu,
       a pop-up or the drawer) the button steps aside; it returns as soon as the page has scrolled on. */
    if (!fabCtas) fabCtas = document.querySelectorAll('a[href*="/cart/add"], button[name="add"], a.btn, button.btn, .spc-btn');
    var fw = fab.offsetWidth || 60, br = fab.getBoundingClientRect();
    var fr = br.width ? br.right : (document.documentElement.clientWidth || vw) - 16, fl = fr - fw, fb = vh - lift, ft = fb - fw, hit = false;
    for (var i = 0; i < fabCtas.length && !hit; i++) {
      var c = fabCtas[i];
      if (c.closest('.sbar, .hdr, .mnav, .gpop, #sp-cart-drawer')) continue;
      var cr = c.getBoundingClientRect();
      if (cr.height > 0 && cr.bottom > ft - 8 && cr.top < fb + 8 && cr.right > fl - 8 && cr.left < fr + 8) hit = true;
    }
    fab.classList.toggle('is-yield', hit);
  }
  var fabTick = false;
  function queueFab() { if (fabTick) return; fabTick = true; requestAnimationFrame(function () { fabTick = false; placeFab(); }); }

  function init() {
    drawer = document.getElementById('sp-cart-drawer');
    var page = document.querySelector('[data-sp-cart-page]');
    if (page) document.body.classList.add('spc-on-cart-page');

    if (drawer) {
      var body = drawer.querySelector('[data-spc-body]');
      bindActions(body);
      onChange(function (st) { renderInto(body, st, { drawer: true }); keepFocusIn(); });
      drawer.addEventListener('click', function (e) { if (e.target.closest('[data-spc-close]')) { e.preventDefault(); closeDrawer(); } });
      document.addEventListener('keydown', onDrawerKey, true);
    }
    if (page) {
      var pb = page.querySelector('[data-spc-body]');
      bindActions(pb);
      onChange(function (st) { renderInto(pb, st, { drawer: false }); });
    }
    onChange(paintCounts);

    document.addEventListener('click', function (e) {
      var t = e.target.closest('#cart-icon-bubble, [data-spc-open]');
      if (!t) return;
      if (openDrawer()) e.preventDefault();
    }, true);

    sync();
    if (document.querySelector('.spc-fab')) {
      window.addEventListener('scroll', queueFab, { passive: true });
      window.addEventListener('resize', queueFab);
      setInterval(function () { fabCtas = null; queueFab(); }, 600);
      /* F10: a bottom bar slides in with a transition; place the button again the moment it has finished */
      document.addEventListener('transitionend', function (e) { if (e.target && e.target.classList && !e.target.classList.contains('spc-fab')) queueFab(); }, true);
      queueFab();
    }
    /* back/forward cache + other tabs */
    window.addEventListener('pageshow', function (e) { if (e.persisted) sync(); });
    window.addEventListener('storage', function (e) { if (e.key === OFF_KEY) sync(); });
  }

  /* ---------- F3 2026-10-12 (optimizer, key home-buy-links-open-cart-drawer) ----------
     "Buy one bag", "Shop the 10-Pack" and "Shop the Stockpile" on the homepage are plain /cart/add links: the tap loaded the
     /cart page and, being a GET, sent no add-to-cart event to Shopify's pixels. The join page already turns the same links
     into ONE POST to /cart/add.js and opens this drawer in place (sp-join.js). This does the same for every such link on
     every other page. Join-page links stay with sp-join.js. Any failure falls back to the link itself. */
  var linkBusy = false;
  function parseAddHref(href) {
    var m = /\/cart\/add\?([^#]*)/.exec(href || ''); if (!m) return null;
    var o = {};
    m[1].split('&').forEach(function (kv) { var p = kv.split('='); try { o[decodeURIComponent(p[0])] = decodeURIComponent(p[1] || ''); } catch (e) {} });
    if (!/^\d+$/.test(o.id || '')) return null;
    return { id: Number(o.id), quantity: Math.max(1, parseInt(o.quantity, 10) || 1), plan: /^\d+$/.test(o.selling_plan || '') ? Number(o.selling_plan) : null };
  }
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href*="/cart/add?"]') : null;
    if (!a || !drawer || document.body.classList.contains('spc-on-cart-page')) return;
    if (a.closest('.spj') && document.querySelector('script[src*="sp-join.js"]')) return; /* the join page handles its own */
    var want = parseAddHref(a.getAttribute('href')); if (!want) return;
    e.preventDefault();
    if (linkBusy) return;
    linkBusy = true; a.setAttribute('aria-busy', 'true');
    var href = a.href;
    function done() { linkBusy = false; a.removeAttribute('aria-busy'); }
    getCart().then(function (cart) {
      var has = (cart.items || []).some(function (i) {
        var p = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan ? Number(i.selling_plan_allocation.selling_plan.id) : null;
        return Number(i.variant_id) === want.id && p === want.plan;
      });
      if (has) { done(); openDrawer(); return null; }
      var item = { id: want.id, quantity: want.quantity }; if (want.plan) item.selling_plan = want.plan;
      /* window.fetch on purpose (not api()): the watcher above opens the drawer and re-reads the cart when the add lands */
      return window.fetch(ROOT + 'cart/add.js', { method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [item] }) })
        .then(function (r) { if (!r.ok) throw new Error('add ' + r.status); return r.json(); })
        .then(function () { done(); });
    }).catch(function () { done(); window.location.href = href; });
  });

  window.SPCart = { sync: sync, open: function () { return openDrawer(); }, close: closeDrawer, setProtectionOff: setOff, state: state, onChange: onChange };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
